/* eslint-disable react/prop-types */
/* eslint-disable react/function-component-definition */
/**
=========================================================
* Material Dashboard 2 React - Stock Management
=========================================================
*/

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import useStock from "hooks/useStock";
import {
  Avatar,
  AvatarGroup,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
const formatDateIndianIST = (isoString) => {
  const date = new Date(isoString);

  // Convert UTC to IST by adding 5 hours 30 minutes (19800000 ms)
  const istDate = new Date(date.getTime());

  return istDate.toLocaleString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true, // Ensures AM/PM format
  });
};

export default function data() {
  const { stockList } = useStock();
  const [data, setData] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);

  const openDialogHandler = (stock) => {
    setSelectedStock(stock);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedStock(null);
  };

  const StockActions = ({ onPreview }) => (
    <MDBox display="flex" justifyContent="center">
      <MDButton variant="contained" color="info" sx={{ mr: 1 }} onClick={onPreview}>
        Preview
      </MDButton>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        BackdropProps={{
          style: { backgroundColor: "hsla(0, 0.00%, 0.00%, 0.12)" }, // Light transparent background
        }}
      >
        <DialogTitle>
          {selectedStock?.name}
          <IconButton onClick={handleClose} sx={{ position: "absolute", right: 10, top: 10 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <MDBox
              sx={{
                bgcolor: "grey.100",
                p: 2,
                borderRadius: 2,
                mb: 2,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <MDTypography variant="subtitle1" fontWeight="bold">
                Total Quantity:
              </MDTypography>
              <MDTypography variant="h6" fontWeight="bold">
                {selectedStock?.total}
              </MDTypography>
            </MDBox>

            <MDTypography variant="h6" gutterBottom>
              Distribution Details
            </MDTypography>

            {selectedStock?.distributers.map((dist, index) => (
              <MDBox key={index} sx={{ border: 1, borderRadius: 2, overflow: "hidden", mb: 2 }}>
                <MDBox sx={{ bgcolor: "grey.200", p: 2, borderBottom: 1 }}>
                  <MDBox
                    sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                  >
                    <MDTypography variant="subtitle1">{dist.distributer}</MDTypography>
                    <Chip label={`Total: ${dist?.total_quantity}`} color="primary" />
                  </MDBox>
                </MDBox>

                <MDBox sx={{ p: 2 }}>
                  <MDTypography variant="body2" color="textSecondary" gutterBottom>
                    Warehouses
                  </MDTypography>
                  <List>
                    {dist.warehouses.map((wh, widx) => (
                      <ListItem
                        key={widx}
                        sx={{
                          bgcolor: "grey.100",
                          borderRadius: 1,
                          mb: 1,
                          p: 2,
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 0.5,
                        }}
                      >
                        <MDTypography fontSize="0.875rem">Warehouse: {wh.warehouse}</MDTypography>

                        <MDTypography fontSize="0.875rem" color="text.secondary">
                          Last Synced: <strong>{formatDateIndianIST(wh.syncAt)}</strong>
                        </MDTypography>

                        <MDTypography fontSize="0.875rem" color="text.secondary">
                          Quantity: <strong>{wh.quantity}</strong>
                        </MDTypography>
                      </ListItem>
                    ))}
                  </List>
                </MDBox>
              </MDBox>
            ))}
          </DialogContentText>
        </DialogContent>
      </Dialog>
    </MDBox>
  );

  const StockInfo = ({ name }) => (
    <MDBox display="flex" alignItems="center" lineHeight={1}>
      <MDTypography display="block" variant="button" fontWeight="medium">
        {name}
      </MDTypography>
    </MDBox>
  );

  const getInitials = (name) => name.charAt(0).toUpperCase();

  const classicColors = [
    "#1E3A8A", // Navy Blue
    "#4F46E5", // Indigo
    "#0369A1", // Deep Blue
    "#334155", // Dark Gray
    "#1E40AF", // Royal Blue
    "#64748B", // Muted Gray-Blue
    "#3B82F6", // Standard Blue
    "#475569", // Soft Dark Gray
    "#2563EB", // Bright Blue
    "#1E293B", // Almost Black-Gray
  ];

  // Function to get color based on index
  const getColor = (index) => classicColors[index % classicColors.length];

  const Distributor = ({ distributers = [] }) => {
    return (
      <MDBox display="flex" alignItems="center">
        <AvatarGroup max={4} sx={{ flexDirection: "row-reverse" }}>
          {distributers.map((item, index) => (
            <Tooltip key={item?.distributer} title={item?.distributer} arrow>
              <Avatar
                sx={{
                  bgcolor: getColor(index),
                  color: "white",
                  fontWeight: "bold",
                  cursor: "pointer",
                  transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                  "&:hover": {
                    transform: "scale(1.2)",
                    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
                  },
                }}
              >
                {item?.distributer?.img ? (
                  <img src={item?.distributer?.img} alt={item?.distributer} />
                ) : (
                  getInitials(item?.distributer)
                )}
              </Avatar>
            </Tooltip>
          ))}
        </AvatarGroup>
      </MDBox>
    );
  };

  const rows = data.map((stock) => ({
    productName: <StockInfo name={stock?.name} />,
    distributor: <Distributor distributers={stock.distributers} />,
    availableQuantity: (
      <MDTypography display="block" variant="button">
        {stock.total}
      </MDTypography>
    ),
    actions: <StockActions onPreview={() => openDialogHandler(stock)} />,
  }));

  // Commented out as requested
  useEffect(() => {
    if (stockList.products) {
      setData(stockList.products);
    }
  }, [stockList]);

  return {
    columns: [
      { Header: "Product Name", accessor: "productName", width: "30%", align: "left" },
      { Header: "Distributor", accessor: "distributor", align: "center" },
      { Header: "Available Quantity", accessor: "availableQuantity", align: "center" },
      { Header: "Actions", accessor: "actions", align: "center" },
    ],
    rows,
  };
}
